/**
 * SuggestionCard — the CSM-connect suggestion widget (class 'suggestion',
 * type 'csm_connect'). A QUIET, single offer to hand the user to their Customer
 * Success team — deliberately NOT a display card and NOT a compact chip: a flat,
 * violet-tinted aside with plain-language copy and quiet text links, so it reads
 * as a helpful offer, never an ad.
 *
 * Every field is untrusted (see partition.ts); this component is defensive and
 * never throws (mirrors reduceTrace / the display cards). No `??`/`?.` — the
 * build toolchain does not transpile them (AGENTS.md); explicit `=== undefined`.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CopilotWidget } from '../lib/copilotTypes'
import { str } from './format'
import { LifebuoyIcon } from '../icons'
import { cx } from '../cx'
import styles from './widgets.module.scss'

/**
 * Known product → i18n label key for the offer's optional context line. Only
 * products in this map get a "…about <topic>" line; an unknown product key is
 * omitted entirely (the raw key is NEVER shown to the user). The label TEXT
 * lives in i18n; only the set of known keys lives in code.
 *
 * Keys mirror the backend's 14 canonical product keys (gen-ai-service
 * business_capabilities.PRODUCT_KEYS) exactly — additive-only.
 */
const PRODUCT_LABEL_KEYS: Record<string, string> = {
  website: 'genAI.copilot-widget-csm-product-website',
  placement: 'genAI.copilot-widget-csm-product-placement',
  gmb: 'genAI.copilot-widget-csm-product-gmb',
  phone: 'genAI.copilot-widget-csm-product-phone',
  booking: 'genAI.copilot-widget-csm-product-booking',
  messaging: 'genAI.copilot-widget-csm-product-messaging',
  payments: 'genAI.copilot-widget-csm-product-payments',
  reviews: 'genAI.copilot-widget-csm-product-reviews',
  review_monitor_ai: 'genAI.copilot-widget-csm-product-review_monitor_ai',
  contacts: 'genAI.copilot-widget-csm-product-contacts',
  chat_agent: 'genAI.copilot-widget-csm-product-chat_agent',
  voice_agent: 'genAI.copilot-widget-csm-product-voice_agent',
  estimate: 'genAI.copilot-widget-csm-product-estimate',
  engagement: 'genAI.copilot-widget-csm-product-engagement'
}

/** resolve the offer's topic label key from `product`, falling back to `interest`; undefined when unknown. */
const resolveTopicKey = (widget: CopilotWidget): string | undefined => {
  const product = str(widget.product)
  if (product !== undefined && PRODUCT_LABEL_KEYS[product] !== undefined) return PRODUCT_LABEL_KEYS[product]
  const interest = Array.isArray(widget.interest) ? widget.interest : []
  for (let i = 0; i < interest.length; i++) {
    const key = str(interest[i])
    if (key !== undefined && PRODUCT_LABEL_KEYS[key] !== undefined) return PRODUCT_LABEL_KEYS[key]
  }
  return undefined
}

const SuggestionCard: React.FC<{ widget: CopilotWidget }> = ({ widget }) => {
  const { t } = useTranslation()

  const productLink = str(widget.product_link)
  const genericLink = str(widget.generic_link)
  // primary prefers the per-product link, falling back to the generic one.
  const primaryHref = productLink !== undefined ? productLink : genericLink
  // no link at all → render nothing (never a dead offer).
  if (primaryHref === undefined) return null

  // secondary "talk to the team" link only when it points somewhere DIFFERENT
  // from the primary (i.e. the primary used product_link) — never a duplicate.
  const secondaryHref = genericLink !== undefined && genericLink !== primaryHref ? genericLink : undefined

  const topicKey = resolveTopicKey(widget)
  const bodyText =
    topicKey !== undefined
      ? t('genAI.copilot-widget-csm-body-about', { topic: t(topicKey) })
      : t('genAI.copilot-widget-csm-body')

  return (
    <div className={styles.offer}>
      <span className={styles.offerGlyphWrap}>
        <LifebuoyIcon className={styles.offerGlyph} />
      </span>
      <div className={styles.offerBody}>
        <span className={styles.offerLead}>{t('genAI.copilot-widget-csm-offer')}</span>
        <span className={styles.offerText}>{bodyText}</span>
        <div className={styles.offerActions}>
          <a className={styles.offerLink} href={primaryHref} target="_blank" rel="noopener noreferrer">
            {t('genAI.copilot-widget-csm-action')}
            <span className={styles.offerLinkArrow} aria-hidden="true">
              →
            </span>
          </a>
          {secondaryHref !== undefined && (
            <a
              className={cx(styles.offerLink, styles.offerLinkSecondary)}
              href={secondaryHref}
              target="_blank"
              rel="noopener noreferrer">
              {t('genAI.copilot-widget-csm-action-generic')}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default SuggestionCard
